import { usePressWithoutDrag } from '@hooks'
import React from 'react'
import { TileBody, TileButton, TileFooter, TileLink } from './BentoTile.styles'
import TileHeader from './TileHeader'
import TileSkeleton from './TileSkeleton'

interface BentoTileProps {
	/** Pre-sized section icon rendered in the header's warm-tint chip. */
	icon: React.ReactNode
	label: string
	count?: React.ReactNode
	/** Footer "view" affordance (e.g. "Open the full itinerary"). */
	footer?: React.ReactNode
	children?: React.ReactNode
	/** Accessible name for the whole clickable tile (required — the tile is a control). */
	ariaLabel: string
	/** Grid column span out of 12. */
	colSpan: number
	/** Optional grid row span (the tall itinerary/accommodations tiles use 2). */
	rowSpan?: number
	/** While this tile's own query loads: swap the body for a skeleton, hide count + footer. */
	loading?: boolean
	/** Skeleton rows to render while loading (defaults to 3). */
	skeletonRows?: number
	/** React-router destination. When set, the tile renders as a `<Link>`. */
	to?: string
	/** Click handler. Used when `to` is not provided (the tile renders as a `<button>`). */
	onClick?: () => void
	className?: string
}

/**
 * The shared bento tile: a clickable surface with a header (icon + label + count
 * + hover arrow), a body, and a footer link. Renders as a router `<Link>` when
 * given `to`, otherwise a `<button>` driven by `onClick`.
 */
const BentoTile: React.FC<BentoTileProps> = ({
	icon,
	label,
	count,
	footer,
	children,
	ariaLabel,
	colSpan,
	rowSpan,
	loading = false,
	skeletonRows,
	to,
	onClick,
	className,
}) => {
	// Some tiles wrap horizontally scrollable rails: a touch/pen swipe that
	// scrolls a rail must not activate the tile, while taps, mouse clicks, and
	// keyboard/AT activation always do. The shared guard owns that distinction.
	const press = usePressWithoutDrag(onClick)

	const inner = (
		<>
			<TileHeader icon={icon} label={label} count={loading ? undefined : count} />
			<TileBody>{loading ? <TileSkeleton rows={skeletonRows} /> : children}</TileBody>
			{!loading && footer != null && <TileFooter>{footer}</TileFooter>}
		</>
	)

	if (to && !loading) {
		return (
			<TileLink
				to={to}
				aria-label={ariaLabel}
				{...press}
				$colSpan={colSpan}
				$rowSpan={rowSpan}
				className={className}
			>
				{inner}
			</TileLink>
		)
	}

	return (
		<TileButton
			type="button"
			{...press}
			aria-label={ariaLabel}
			$colSpan={colSpan}
			$rowSpan={rowSpan}
			className={className}
		>
			{inner}
		</TileButton>
	)
}

export default BentoTile
